// v1.2 - Integrated Hybrid Project Hierarchies & Cascading Geographic Selector Dialog (cache-bust: v1.3)
import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    IconButton,
    Stack,
    Chip,
    Paper,
    Alert,
    Tooltip,
    Tabs,
    Tab,
    Autocomplete,
    Checkbox,
    FormControlLabel,
    Divider,
    alpha,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import axios, { endpoints } from 'src/utils/axios';
import { userConfig } from '../users/userConfig';
import HierarchyTree from './HierarchyTree';
import DesignationHierarchyTree from './DesignationHierarchyTree';

// ── Empty state drop zone component ────────────────────────────────────
function EmptyDropZone({ onDropUser }) {
    const [isDragOver, setIsDragOver] = useState(false);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
        e.dataTransfer.dropEffect = 'copy';
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const userId = e.dataTransfer.getData('userId');
        const userName = e.dataTransfer.getData('userName');

        if (userId && userName) {
            const user = {
                _id: userId,
                firstName: userName.split(' ')[0],
                lastName: userName.split(' ').slice(1).join(' ')
            };
            onDropUser(user);
        }
    };

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            height="100%"
            spacing={2}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
                border: 3,
                borderStyle: 'dashed',
                borderColor: isDragOver ? 'success.main' : 'divider',
                borderRadius: 2,
                p: 4,
                bgcolor: isDragOver ? 'success.lighter' : 'transparent',
                transition: 'all 0.2s ease',
            }}
        >
            <Iconify
                icon={isDragOver ? "solar:download-square-bold" : "solar:users-group-rounded-bold"}
                width={120}
                sx={{ color: isDragOver ? 'success.main' : 'text.disabled' }}
            />
            <Typography variant="h6" color={isDragOver ? 'success.main' : 'text.secondary'}>
                {isDragOver ? 'Drop to set as Project Leader' : 'No team assigned yet'}
            </Typography>
            <Typography color="text.secondary">
                {isDragOver ? 'Release to add' : 'Drag a user here or click a user from the left'}
            </Typography>
        </Stack>
    );
}

EmptyDropZone.propTypes = {
    onDropUser: PropTypes.func.isRequired,
};

// ── Cascading Geographic User Selector Dialog ───────────────────────────
function GeoUserSelectorDialog({ open, onClose, designation, users, onConfirm, initialSelectedUsers, projectStates, projectCities, excludeUserIds }) {
    const theme = useTheme();
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedCities, setSelectedCities] = useState([]);
    const [selectedBlocks, setSelectedBlocks] = useState([]);
    const [selectedLocalities, setSelectedLocalities] = useState([]);

    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [blocks, setBlocks] = useState([]);
    const [localities, setLocalities] = useState([]);

    const [selectedUserIds, setSelectedUserIds] = useState([]);

    // Initialize selections on dialog open
    useEffect(() => {
        if (open) {
            setSelectedUserIds(initialSelectedUsers.map(u => u._id || u));
            setSelectedStates([]);
            setSelectedCities([]);
            setSelectedBlocks([]);
            setSelectedLocalities([]);
        }
    }, [open, initialSelectedUsers]);

    // Fetch States on open
    useEffect(() => {
        const fetchStates = async () => {
            if (projectStates && projectStates.length > 0) {
                setStates(projectStates);
                return;
            }
            try {
                const res = await axios.get(`${endpoints.stateMaster.list}?status=active&limit=0`);
                setStates(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        if (open) {
            fetchStates();
        }
    }, [open, projectStates]);

    // Fetch Cities based on selected states
    useEffect(() => {
        const fetchCities = async () => {
            if (selectedStates.length === 0) {
                setCities([]);
                setSelectedCities([]);
                return;
            }
            try {
                const stateIds = selectedStates.map(s => s._id).join(',');
                const res = await axios.get(`${endpoints.cityMaster.list}?status=active&limit=0&state=${stateIds}`);
                let fetchedCities = res.data?.data || [];
                if (projectCities && projectCities.length > 0) {
                    const projectCityIds = projectCities.map(c => c._id || c?.id || c).filter(Boolean).map(String);
                    fetchedCities = fetchedCities.filter(c => projectCityIds.includes(String(c._id)));
                }
                setCities(fetchedCities);
            } catch (err) {
                console.error(err);
            }
        };
        fetchCities();
    }, [selectedStates, projectCities]);

    // Fetch Blocks based on selected cities
    useEffect(() => {
        const fetchBlocks = async () => {
            if (selectedCities.length === 0) {
                setBlocks([]);
                setSelectedBlocks([]);
                return;
            }
            try {
                const cityIds = selectedCities.map(c => c._id).join(',');
                const res = await axios.get(`${endpoints.blockMaster.list}?status=active&limit=0&city=${cityIds}`);
                setBlocks(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchBlocks();
    }, [selectedCities]);

    // Fetch Localities based on selected blocks
    useEffect(() => {
        const fetchLocalities = async () => {
            if (selectedBlocks.length === 0) {
                setLocalities([]);
                setSelectedLocalities([]);
                return;
            }
            try {
                const blockIds = selectedBlocks.map(b => b._id).join(',');
                const res = await axios.get(`${endpoints.localityMaster.list}?status=active&limit=0&block=${blockIds}`);
                setLocalities(res.data?.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchLocalities();
    }, [selectedBlocks]);

    // Filter users holding selected designation + matching working location profile
    const filteredUsers = useMemo(() => {
        const designationId = designation?._id || designation;
        if (!designationId) return [];

        return users.filter(user => {
            // Exclude users already assigned elsewhere in the tree/levels
            if (excludeUserIds && excludeUserIds.has(String(user._id))) {
                return false;
            }

            // Match designation
            const userDesignationId = user.designation?._id || user.designation;
            if (String(userDesignationId) !== String(designationId)) {
                return false;
            }

            // Geographic cascading filtering
            if (selectedStates.length > 0) {
                const userStates = user.positioningStates?.map(s => s._id || s) || [];
                const match = userStates.some(id => selectedStates.map(s => s._id).includes(id));
                if (!match) return false;
            }

            if (selectedCities.length > 0) {
                const userCities = user.positioningCities?.map(c => c._id || c) || [];
                const match = userCities.some(id => selectedCities.map(c => c._id).includes(id));
                if (!match) return false;
            }

            if (selectedBlocks.length > 0) {
                const userBlocks = user.positioningBlocks?.map(b => b._id || b) || [];
                const match = userBlocks.some(id => selectedBlocks.map(b => b._id).includes(id));
                if (!match) return false;
            }

            if (selectedLocalities.length > 0) {
                const userLocalities = user.positioningLocalities?.map(l => l._id || l) || [];
                const match = userLocalities.some(id => selectedLocalities.map(l => l._id).includes(id));
                if (!match) return false;
            }

            return true;
        });
    }, [designation, users, selectedStates, selectedCities, selectedBlocks, selectedLocalities, excludeUserIds]);

    const handleToggleUser = (userId) => {
        setSelectedUserIds(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const handleSelectAll = () => {
        const allFilteredIds = filteredUsers.map(u => u._id);
        const allSelected = allFilteredIds.every(id => selectedUserIds.includes(id));

        if (allSelected) {
            setSelectedUserIds(prev => prev.filter(id => !allFilteredIds.includes(id)));
        } else {
            setSelectedUserIds(prev => {
                const toAdd = allFilteredIds.filter(id => !prev.includes(id));
                return [...prev, ...toAdd];
            });
        }
    };

    const handleConfirm = () => {
        const selectedObjects = users.filter(u => selectedUserIds.includes(u._id));
        onConfirm(selectedObjects);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        Assign Staff: {designation?.designation_name || 'Designation'} Level
                    </Typography>
                    <IconButton onClick={onClose}>
                        <Iconify icon="solar:close-circle-bold" />
                    </IconButton>
                </Stack>
            </DialogTitle>
            
            <DialogContent dividers sx={{ pt: 2.5 }}>
                {/* Geographic Filters Panel */}
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Location Filter Cascade
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            multiple
                            limitTags={1}
                            options={states}
                            getOptionLabel={(option) => option.state_name || ''}
                            value={selectedStates}
                            onChange={(e, v) => setSelectedStates(v)}
                            renderInput={(params) => <TextField {...params} label="States" size="small" />}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            multiple
                            limitTags={1}
                            options={cities}
                            getOptionLabel={(option) => option.city_name || ''}
                            value={selectedCities}
                            onChange={(e, v) => setSelectedCities(v)}
                            renderInput={(params) => <TextField {...params} label="Cities" size="small" placeholder={selectedStates.length === 0 ? 'Select State first' : ''} />}
                            disabled={selectedStates.length === 0}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            multiple
                            limitTags={1}
                            options={blocks}
                            getOptionLabel={(option) => option.block_name || ''}
                            value={selectedBlocks}
                            onChange={(e, v) => setSelectedBlocks(v)}
                            renderInput={(params) => <TextField {...params} label="Blocks" size="small" placeholder={selectedCities.length === 0 ? 'Select City first' : ''} />}
                            disabled={selectedCities.length === 0}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            multiple
                            limitTags={1}
                            options={localities}
                            getOptionLabel={(option) => option.locality_name || ''}
                            value={selectedLocalities}
                            onChange={(e, v) => setSelectedLocalities(v)}
                            renderInput={(params) => <TextField {...params} label="Localities" size="small" placeholder={selectedBlocks.length === 0 ? 'Select Block first' : ''} />}
                            disabled={selectedBlocks.length === 0}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                        />
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Users List with Checkboxes */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Eligible Employees ({filteredUsers.length} found)
                    </Typography>
                    {filteredUsers.length > 0 && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filteredUsers.every(u => selectedUserIds.includes(u._id))}
                                    indeterminate={
                                        filteredUsers.some(u => selectedUserIds.includes(u._id)) &&
                                        !filteredUsers.every(u => selectedUserIds.includes(u._id))
                                    }
                                    onChange={handleSelectAll}
                                />
                            }
                            label="Select All"
                        />
                    )}
                </Stack>

                {filteredUsers.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.neutral', borderRadius: 1.5 }}>
                        <Typography color="text.secondary">
                            No employees with this designation found matching the location filters.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2} sx={{ maxHeight: 350, overflowY: 'auto', pr: 0.5 }}>
                        {filteredUsers.map((user) => {
                            const isSelected = selectedUserIds.includes(user._id);
                            return (
                                <Grid item xs={12} sm={6} key={user._id}>
                                    <Paper
                                        variant="outlined"
                                        onClick={() => handleToggleUser(user._id)}
                                        sx={{
                                            p: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            borderColor: isSelected ? 'primary.main' : 'divider',
                                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
                                            '&:hover': {
                                                borderColor: 'primary.main',
                                                bgcolor: alpha(theme.palette.primary.main, 0.02)
                                            }
                                        }}
                                    >
                                        <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                                        <Avatar sx={{ bgcolor: 'info.main', width: 36, height: 36, mr: 1.5 }}>
                                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                        </Avatar>
                                        <ListItemText
                                            primary={`${user.firstName} ${user.lastName}`}
                                            secondary={user.employeeId || 'No ID'}
                                            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
                                            secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                        />
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="inherit">
                    Cancel
                </Button>
                <Button onClick={handleConfirm} variant="contained" color="success">
                    Confirm Selections ({selectedUserIds.length})
                </Button>
            </DialogActions>
        </Dialog>
    );
}

GeoUserSelectorDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    designation: PropTypes.object,
    users: PropTypes.array.isRequired,
    onConfirm: PropTypes.func.isRequired,
    initialSelectedUsers: PropTypes.array.isRequired,
    projectStates: PropTypes.array,
    projectCities: PropTypes.array,
    excludeUserIds: PropTypes.instanceOf(Set),
};

// ── Helper functions for Designation Hierarchy Tree ────────────────────
const getDesignationTreeUserIdsExcludingNode = (node, excludeNodeId, ids = new Set()) => {
    if (!node) return ids;
    if (node.id !== excludeNodeId && node.assignedUsers && Array.isArray(node.assignedUsers)) {
        node.assignedUsers.forEach(u => {
            const uId = u._id || u.id || u;
            if (uId) ids.add(String(uId));
        });
    }
    if (node.children) {
        node.children.forEach(child => getDesignationTreeUserIdsExcludingNode(child, excludeNodeId, ids));
    }
    return ids;
};

const getDesignationLevelsUserIdsExcludingIndex = (levels, excludeIdx) => {
    const ids = new Set();
    levels.forEach((lvl, idx) => {
        if (idx !== excludeIdx && lvl.assignedUsers && Array.isArray(lvl.assignedUsers)) {
            lvl.assignedUsers.forEach(u => {
                const uId = u._id || u.id || u;
                if (uId) ids.add(String(uId));
            });
        }
    });
    return ids;
};
const getAssignedUserIds = (node, ids = new Set(), leaderIdObj = { id: null }) => {
    if (!node) return ids;
    if (node.userId) {
        ids.add(node.userId);
        if (node.role === 'leader') {
            leaderIdObj.id = node.userId;
        }
    }
    if (node.children) {
        node.children.forEach(child => getAssignedUserIds(child, ids, leaderIdObj));
    }
    return ids;
};

const getDesignationTreeUserIds = (node, ids = new Set()) => {
    if (!node) return ids;
    if (node.assignedUsers && Array.isArray(node.assignedUsers)) {
        node.assignedUsers.forEach(u => {
            const uId = u._id || u.id || u;
            if (uId) ids.add(String(uId));
        });
    }
    if (node.children) {
        node.children.forEach(child => getDesignationTreeUserIds(child, ids));
    }
    return ids;
};

const findNodeById = (currentNode, id) => {
    if (!currentNode) return null;
    if (currentNode.id === id) return currentNode;
    if (currentNode.children) {
        let found = null;
        currentNode.children.some((child) => {
            found = findNodeById(child, id);
            return found !== null;
        });
        return found;
    }
    return null;
};

const updateAssignedUsersInNode = (currentNode, nodeId, selectedUsers) => {
    if (!currentNode) return null;
    if (currentNode.id === nodeId) {
        return {
            ...currentNode,
            assignedUsers: selectedUsers
        };
    }
    if (currentNode.children) {
        return {
            ...currentNode,
            children: currentNode.children.map(child =>
                updateAssignedUsersInNode(child, nodeId, selectedUsers)
            )
        };
    }
    return currentNode;
};

// ── Main Component ────────────────────────────────────────────────────
export default function ProjectAssignmentModal({
    open,
    onClose,
    project,
    onSaveSuccess,
    isMigrationContext = false,
    stagedAssignment = null,
    onSaveStaged = null,
    migrationInfo = null,
    EntityCreationDialog
}) {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [users, setUsers] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [userHierarchyTree, setUserHierarchyTree] = useState(null);
    const [designationHierarchyTree, setDesignationHierarchyTree] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [roleTab, setRoleTab] = useState('all');
    const [isDragging, setIsDragging] = useState(false);
    const [formCreators, setFormCreators] = useState([]);
    const [rightTab, setRightTab] = useState('hierarchy');

    // Hybrid Hierarchy states
    const [hierarchyMode, setHierarchyMode] = useState('user_based');
    const [designationLevels, setDesignationLevels] = useState([]);
    const [initialHierarchyMode, setInitialHierarchyMode] = useState(null);
    const [pendingTabChange, setPendingTabChange] = useState(null);
    const [wizardOpen, setWizardOpen] = useState(false);
    const [wizardForms, setWizardForms] = useState([]);
    const [currentWizardStep, setCurrentWizardStep] = useState(0);
    const [wizardRights, setWizardRights] = useState({});
    
    // Geographic selector dialog states
    const [geoSelectorOpen, setGeoSelectorOpen] = useState(false);
    const [activeLevelIdx, setActiveLevelIdx] = useState(null);
    const [geoTargetNodeId, setGeoTargetNodeId] = useState(null);

    const [copyDialogOpen, setCopyDialogOpen] = useState(false);
    const [copyProjectLoading, setCopyProjectLoading] = useState(false);
    const [projectsForCopy, setProjectsForCopy] = useState([]);
    const [selectedProjectToCopy, setSelectedProjectToCopy] = useState(null);
    const [quickCreateOpen, setQuickCreateOpen] = useState(false);

    // Fetch designations
    const fetchDesignations = useCallback(async () => {
        try {
            const res = await axios.get(`${endpoints.designationMaster.list}?status=active&limit=0`);
            setDesignations(res.data?.data || []);
        } catch (err) {
            console.error('Failed to load designations', err);
        }
    }, []);

    // Fetch users
    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                limit: 0,
                status: 'active'
            };
            if (!isMigrationContext) {
                if (project?.states && project.states.length > 0) {
                    params.positioningStates = project.states.map(s => s?._id || s?.id || s).filter(Boolean).join(',');
                }
                if (project?.cities && project.cities.length > 0) {
                    params.positioningCities = project.cities.map(c => c?._id || c?.id || c).filter(Boolean).join(',');
                }
            }
            const response = await axios.get(endpoints.user.list, { params });
            setUsers(response.data.data || []);
        } catch (error) {
            console.error('Failed to fetch users', error);
            enqueueSnackbar('Failed to load users', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar, project, isMigrationContext]);

    // Fetch existing assignment
    const fetchAssignment = useCallback(async () => {
        if (!project?._id) return;

        if (isMigrationContext && stagedAssignment) {
            const data = stagedAssignment;
            if (data.hierarchyMode) {
                setHierarchyMode(data.hierarchyMode);
                setInitialHierarchyMode(data.hierarchyMode);
            } else {
                setInitialHierarchyMode('user_based');
            }
            if (data.hierarchyTree) {
                const populateUserTree = (node) => {
                    if (!node) return;
                    const uId = node.userId?._id || node.userId;
                    if (uId) {
                        const matchedUser = users.find(u => u._id === uId.toString());
                        if (matchedUser) {
                            node.name = `${matchedUser.firstName} ${matchedUser.lastName}`;
                            node.userId = matchedUser;
                        }
                    }
                    if (node.children) {
                        node.children.forEach(populateUserTree);
                    }
                };
                const treeCopy = JSON.parse(JSON.stringify(data.hierarchyTree));
                if (data.hierarchyMode === 'designation_based') {
                    setDesignationHierarchyTree(treeCopy);
                } else {
                    populateUserTree(treeCopy);
                    setUserHierarchyTree(treeCopy);
                }
            }
            if (data.designationLevels) {
                const populatedLevels = data.designationLevels.map(lvl => {
                    const matchedDes = designations.find(d => d._id === (lvl.designationId?._id || lvl.designationId));
                    return {
                        ...lvl,
                        designationId: matchedDes || lvl.designationId,
                        assignedUsers: Array.isArray(lvl.assignedUsers)
                            ? lvl.assignedUsers.map(uId => users.find(u => u._id === (uId?._id || uId)) || uId).filter(Boolean)
                            : []
                    };
                });
                setDesignationLevels(populatedLevels);
            }
            if (data.formCreators) {
                setFormCreators(
                    data.formCreators
                        .map(fcId => users.find(u => u._id === (fcId?._id || fcId)) || fcId)
                        .filter(Boolean)
                );
            }
            return;
        }

        try {
            const response = await axios.get(endpoints.projectAssignment.getByProject(project._id));
            const data = response.data?.data;
            if (data) {
                if (data.hierarchyMode) {
                    setHierarchyMode(data.hierarchyMode);
                    setInitialHierarchyMode(data.hierarchyMode);
                } else {
                    setInitialHierarchyMode('user_based');
                }
                if (data.hierarchyTree) {
                    if (data.hierarchyMode === 'designation_based') {
                        setDesignationHierarchyTree(data.hierarchyTree);
                    } else {
                        setUserHierarchyTree(data.hierarchyTree);
                    }
                }
                if (data.designationLevels) {
                    // Populate levels with full designation identities from loaded list
                    const populatedLevels = data.designationLevels.map(lvl => {
                        const matchedDes = designations.find(d => d._id === (lvl.designationId?._id || lvl.designationId));
                        return {
                            ...lvl,
                            designationId: matchedDes || lvl.designationId,
                            assignedUsers: Array.isArray(lvl.assignedUsers)
                                ? lvl.assignedUsers.map(uId => users.find(u => u._id === (uId?._id || uId)) || uId).filter(Boolean)
                                : []
                        };
                    });
                    setDesignationLevels(populatedLevels);
                }
                if (data.formCreators) {
                    setFormCreators(
                        data.formCreators
                            .map(fcId => users.find(u => u._id === (fcId?._id || fcId)) || fcId)
                            .filter(Boolean)
                    );
                }
            } else {
                setInitialHierarchyMode('user_based');
            }
        } catch (error) {
            console.error('Failed to fetch assignment', error);
            setInitialHierarchyMode('user_based');
        }
    }, [project, designations, users, isMigrationContext, stagedAssignment]);

    // Load data on mount / open
    useEffect(() => {
        if (open) {
            fetchDesignations().then(() => {
                fetchUsers();
            });
        }
    }, [open, fetchUsers, fetchDesignations]);

    // Fetch assignment once users and designations are loaded
    useEffect(() => {
        if (open && users.length > 0 && designations.length > 0) {
            fetchAssignment();
        }
    }, [open, users, designations, fetchAssignment]);

    const handleDragStart = (e, user) => {
        setIsDragging(true);
        e.dataTransfer.setData('userId', user._id);
        e.dataTransfer.setData('userName', `${user.firstName} ${user.lastName}`);
        e.dataTransfer.effectAllowed = 'copy';
    };

    const handleDragEnd = () => {
        setIsDragging(false);
    };

    const handleCreateUser = async (formData) => {
        try {
            const newUser = userConfig.transformPayload ? userConfig.transformPayload(formData) : formData;
            await axios.post(endpoints.user.create, newUser);
            fetchUsers();
            enqueueSnackbar('User created successfully!', { variant: 'success' });
            setQuickCreateOpen(false);
        } catch (err) {
            console.error('Failed to create user:', err);
            enqueueSnackbar(err.message || 'Failed to create user', { variant: 'error' });
        }
    };

    const quickAddFields = userConfig.form?.fields?.flatMap(section => section.fields || []) || [];

    const handleOpenCopyDialog = async () => {
        setCopyDialogOpen(true);
        try {
            setCopyProjectLoading(true);
            const response = await axios.get(endpoints.project.list, {
                params: {
                    limit: 0,
                    status: 'active'
                }
            });
            const activeProjects = (response.data.data || []).filter(p => p._id !== project?._id);
            setProjectsForCopy(activeProjects);
        } catch (error) {
            console.error('Failed to fetch projects for copy', error);
            enqueueSnackbar('Failed to load projects', { variant: 'error' });
        } finally {
            setCopyProjectLoading(false);
        }
    };

    const handleCopyHierarchy = async () => {
        if (!selectedProjectToCopy) return;
        try {
            setCopyProjectLoading(true);
            const response = await axios.get(endpoints.projectAssignment.getByProject(selectedProjectToCopy._id));
            const data = response.data?.data;
            if (data) {
                if (data.hierarchyTree && hierarchyMode === 'user_based') {
                    if (userHierarchyTree && !window.confirm('This will replace the current hierarchy. Are you sure?')) {
                        setCopyProjectLoading(false);
                        return;
                    }
                    setUserHierarchyTree(data.hierarchyTree);
                    setFormCreators(data.formCreators ? data.formCreators.map(fcId => users.find(u => u._id === fcId) || fcId).filter(Boolean) : []);
                    enqueueSnackbar('Hierarchy tree copied successfully!', { variant: 'success' });
                } else if (data.designationLevels && hierarchyMode === 'designation_based') {
                    if (designationLevels.length > 0 && !window.confirm('This will replace current designation levels. Are you sure?')) {
                        setCopyProjectLoading(false);
                        return;
                    }
                    const populatedLevels = data.designationLevels.map(lvl => {
                        const matchedDes = designations.find(d => d._id === (lvl.designationId?._id || lvl.designationId));
                        return {
                            ...lvl,
                            designationId: matchedDes || lvl.designationId,
                            assignedUsers: lvl.assignedUsers.map(uId => users.find(u => u._id === uId) || uId).filter(Boolean)
                        };
                    });
                    setDesignationLevels(populatedLevels);
                    setFormCreators(data.formCreators ? data.formCreators.map(fcId => users.find(u => u._id === fcId) || fcId).filter(Boolean) : []);
                    enqueueSnackbar('Designation levels copied successfully!', { variant: 'success' });
                } else {
                    enqueueSnackbar('No compatible hierarchy data found in project.', { variant: 'warning' });
                }
                setCopyDialogOpen(false);
                setSelectedProjectToCopy(null);
            }
        } catch (error) {
            console.error('Failed to copy assignment', error);
            enqueueSnackbar('Failed to copy hierarchy', { variant: 'error' });
        } finally {
            setCopyProjectLoading(false);
        }
    };

    // Handle save
    // Helper: get unique designations from tree and levels
    const getUniqueDesignations = useCallback(() => {
        const list = [];
        const seen = new Set();
        const add = (id, name) => {
            if (!id) return;
            const idStr = id.toString();
            if (!seen.has(idStr)) {
                seen.add(idStr);
                list.push({ designationId: idStr, designationName: name });
            }
        };

        const traverse = (n) => {
            if (!n) return;
            if (n.designationId) {
                const desId = n.designationId._id || n.designationId;
                const desName = n.designationId.designation_name || n.name || '';
                add(desId, desName);
            }
            if (n.children) {
                n.children.forEach(traverse);
            }
        };
        traverse(designationHierarchyTree);

        if (designationLevels && Array.isArray(designationLevels)) {
            designationLevels.forEach(lvl => {
                if (lvl.designationId) {
                    const desId = lvl.designationId._id || lvl.designationId;
                    const desName = lvl.designationId.designation_name || '';
                    add(desId, desName);
                }
            });
        }

        return list;
    }, [designationHierarchyTree, designationLevels]);

    // Helper: get flattened list of users from user tree
    const getFlattenedUsers = useCallback(() => {
        const result = [];
        const traverse = (node, depth = 0) => {
            if (!node) return;
            const userDetails = users.find(u => {
                const uId = u._id || u.id;
                return uId && uId.toString() === node.userId?.toString();
            });

            if (userDetails) {
                const systemRole = userDetails.mainDbRole || userDetails.role || 'moderator';
                const isAdminRole = systemRole === 'admin' || systemRole === 'super_admin';

                result.push({
                    userId: node.userId,
                    name: node.userName || node.name || `${userDetails.firstName} ${userDetails.lastName}`,
                    role: node.role || userDetails.role || 'member',
                    systemRole,
                    isAdmin: isAdminRole,
                    depth,
                    email: userDetails.email || ''
                });
            }
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => traverse(child, depth + 1));
            }
        };
        traverse(userHierarchyTree);
        return result;
    }, [userHierarchyTree, users]);

    const handleHierarchyModeChange = (e, nextMode) => {
        if (nextMode === hierarchyMode) return;
        setPendingTabChange(nextMode);
    };

    const handleConfirmTabChange = () => {
        if (pendingTabChange) {
            setHierarchyMode(pendingTabChange);
            if (pendingTabChange === 'user_based') {
                setUserHierarchyTree(null);
                setFormCreators([]);
            } else {
                setDesignationHierarchyTree(null);
                setFormCreators([]);
            }
            setPendingTabChange(null);
            enqueueSnackbar('Hierarchy type changed and tree structure reset. Please build the new hierarchy.', { variant: 'info' });
        }
    };

    const handleWizardRightChange = (formId, targetId, rightKey, checked, isUserOverride = false) => {
        setWizardRights(prev => {
            const formRights = prev[formId] || { rightsMap: [], designationTemplates: [] };
            if (isUserOverride) {
                const rightsMap = [...(formRights.rightsMap || [])];
                const idx = rightsMap.findIndex(entry => entry.userId === targetId);
                const userRights = idx !== -1 ? rightsMap[idx].rights : {
                    viewAll: false, view: false, viewGeography: false,
                    viewCountry: false, viewState: false, viewCity: false, viewBlock: false, viewLocality: false,
                    edit: false, add: false, delete: false, approve: false, import: false, export: false
                };
                const newRights = { ...userRights, [rightKey]: checked };

                if (rightKey === 'viewAll' && checked) {
                    newRights.view = false;
                    newRights.viewGeography = false;
                    newRights.viewCountry = false;
                    newRights.viewState = false;
                    newRights.viewCity = false;
                    newRights.viewBlock = false;
                    newRights.viewLocality = false;
                }
                if (['view', 'viewGeography', 'viewCountry', 'viewState', 'viewCity', 'viewBlock', 'viewLocality'].includes(rightKey) && checked) {
                    newRights.viewAll = false;
                }

                if (idx !== -1) {
                    rightsMap[idx] = { userId: targetId, rights: newRights };
                } else {
                    rightsMap.push({ userId: targetId, rights: newRights });
                }
                return {
                    ...prev,
                    [formId]: { ...formRights, rightsMap }
                };
            }

            const designationTemplates = [...(formRights.designationTemplates || [])];
            const idx = designationTemplates.findIndex(entry => entry.designationId === targetId);
            if (idx !== -1) {
                const newRights = { ...designationTemplates[idx].rights, [rightKey]: checked };
                
                if (rightKey === 'viewAll' && checked) {
                    newRights.view = false;
                    newRights.viewGeography = false;
                    newRights.viewCountry = false;
                    newRights.viewState = false;
                    newRights.viewCity = false;
                    newRights.viewBlock = false;
                    newRights.viewLocality = false;
                }
                if (['view', 'viewGeography', 'viewCountry', 'viewState', 'viewCity', 'viewBlock', 'viewLocality'].includes(rightKey) && checked) {
                    newRights.viewAll = false;
                }

                designationTemplates[idx] = { ...designationTemplates[idx], rights: newRights };
            }
            return {
                ...prev,
                [formId]: { ...formRights, designationTemplates }
            };
        });
    };

    const handleWizardSave = async () => {
        try {
            setSaving(true);
            
            const payload = {
                projectId: project._id,
                hierarchyMode,
            };

            if (hierarchyMode === 'user_based') {
                const assignedUserIds = getAssignedUserIds(userHierarchyTree);
                const validFormCreators = formCreators.filter(fc => assignedUserIds.has(fc._id));
                
                payload.hierarchyTree = userHierarchyTree;
                payload.formCreators = validFormCreators.map(user => user._id);
            } else {
                payload.hierarchyTree = designationHierarchyTree;
                payload.designationLevels = designationLevels.map(lvl => ({
                    level: lvl.level,
                    designationId: lvl.designationId?._id || lvl.designationId,
                    assignedUsers: lvl.assignedUsers.map(u => u._id || u)
                }));
                payload.formCreators = formCreators.map(user => user._id);
            }

            if (isMigrationContext) {
                const stagedFormRights = wizardForms.map(form => {
                    const rightsData = wizardRights[form._id] || {};
                    return {
                        formId: form._id,
                        rightsMap: rightsData.rightsMap || [],
                        designationTemplates: rightsData.designationTemplates || [],
                        rightsSwitches: rightsData.rightsSwitches || form.rightsSwitches
                    };
                });
                
                payload.stagedFormRights = stagedFormRights;
                
                if (onSaveStaged) {
                    onSaveStaged(payload);
                }
                enqueueSnackbar('Project assignment changes and form rights staged successfully!', { variant: 'success' });
                setWizardOpen(false);
                onClose();
                return;
            }

            await axios.post(endpoints.projectAssignment.createOrUpdate, payload);
            
            await Promise.all(
                wizardForms.map(async (form) => {
                    const rightsData = wizardRights[form._id];
                    if (rightsData) {
                        await axios.put(`/api/admin/forms/${form._id}/rights`, { 
                            rightsMap: rightsData.rightsMap || [],
                            designationTemplates: rightsData.designationTemplates || [],
                            rightsSwitches: rightsData.rightsSwitches || form.rightsSwitches
                        });
                    }
                })
            );

            enqueueSnackbar('Project assignment and form rights saved successfully!', { variant: 'success' });
            setWizardOpen(false);
            if (onSaveSuccess) {
                onSaveSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Wizard save error:', error);
            enqueueSnackbar('Failed to save assignment and form rights.', { variant: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async () => {
        try {
            const hasModeChanged = hierarchyMode !== initialHierarchyMode;
            if (hasModeChanged && !wizardOpen) {
                setSaving(true);
                const res = await axios.post(endpoints.forms.list, { projectId: project._id, limit: 0 });
                const formsList = res.data?.data || [];
                setSaving(false);

                if (formsList.length > 0) {
                    setSaving(true);
                    const fetchedRights = {};
                    const uniqueDes = hierarchyMode === 'designation_based' ? getUniqueDesignations() : [];
                    const flattenedUsers = hierarchyMode !== 'designation_based' ? getFlattenedUsers() : [];

                    await Promise.all(
                        formsList.map(async (form) => {
                            try {
                                const rightsRes = await axios.get(`/api/admin/forms/${form._id}/rights`);
                                const existingRights = rightsRes.data.data || [];
                                const fetchedTemplates = rightsRes.data.designationTemplates || [];
                                
                                let designationTemplates = [];
                                let rightsMap = [];
                                
                                if (hierarchyMode === 'designation_based') {
                                    designationTemplates = uniqueDes.map(des => {
                                        const matched = fetchedTemplates.find(t => t.designationId?.toString() === des.designationId.toString() || t.refId?.toString() === des.designationId.toString());
                                        return {
                                            designationId: des.designationId,
                                            designationName: des.designationName,
                                            rights: matched ? matched.rights : {
                                                viewAll: false, viewGeography: false, viewCountry: false,
                                                viewState: false, viewCity: false, viewBlock: false, viewLocality: false,
                                                view: false, add: false, edit: false, delete: false, approve: false, import: false, export: false
                                            }
                                        };
                                    });
                                } else {
                                    rightsMap = flattenedUsers.map(user => {
                                        const matched = existingRights.find(r => (r.userId?._id || r.userId)?.toString() === user.userId?.toString());
                                        const isSystemAdmin = user.systemRole === 'admin' || user.systemRole === 'super_admin';
                                        
                                        return {
                                            userId: user.userId,
                                            rights: matched ? matched.rights : {
                                                viewAll: isSystemAdmin, view: false, viewGeography: false,
                                                viewCountry: isSystemAdmin, viewState: isSystemAdmin, viewCity: isSystemAdmin, viewBlock: isSystemAdmin, viewLocality: isSystemAdmin,
                                                edit: isSystemAdmin, add: isSystemAdmin, delete: isSystemAdmin, approve: isSystemAdmin, import: isSystemAdmin, export: isSystemAdmin
                                            }
                                        };
                                    });
                                }
                                
                                fetchedRights[form._id] = {
                                    rightsMap,
                                    designationTemplates,
                                    rightsSwitches: form.rightsSwitches || { import: false, export: true, approve: false }
                                };
                            } catch (err) {
                                console.error(`Failed to fetch rights for form ${form._id}`, err);
                            }
                        })
                    );
                    setWizardRights(fetchedRights);
                    setWizardForms(formsList);
                    setCurrentWizardStep(0);
                    setWizardOpen(true);
                    setSaving(false);
                    return;
                }
            }

            setSaving(true);

            const payload = {
                projectId: project._id,
                hierarchyMode,
            };

            if (hierarchyMode === 'user_based') {
                const assignedUserIds = getAssignedUserIds(userHierarchyTree);
                const validFormCreators = formCreators.filter(fc => assignedUserIds.has(fc._id));
                
                payload.hierarchyTree = userHierarchyTree;
                payload.formCreators = validFormCreators.map(user => user._id);
            } else {
                payload.hierarchyTree = designationHierarchyTree;
                payload.designationLevels = designationLevels.map(lvl => ({
                    level: lvl.level,
                    designationId: lvl.designationId?._id || lvl.designationId,
                    assignedUsers: lvl.assignedUsers.map(u => u._id || u)
                }));
                payload.formCreators = formCreators.map(user => user._id);
            }

            if (isMigrationContext) {
                if (onSaveStaged) {
                    onSaveStaged(payload);
                }
                enqueueSnackbar('Project assignment changes staged successfully!', { variant: 'success' });
                onClose();
                return;
            }

            await axios.post(endpoints.projectAssignment.createOrUpdate, payload);
            enqueueSnackbar('Project assignment saved successfully!', { variant: 'success' });

            if (onSaveSuccess) {
                onSaveSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Save error:', error);
            enqueueSnackbar(error.response?.data?.message || 'Failed to save assignment', {
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    // Handle adding user to tree (first user = leader, click to add directly)
    const handleAddUserToTree = (user) => {
        if (!user) return;

        const newNode = {
            id: `node-${Date.now()}`,
            userId: user._id,
            name: `${user.firstName} ${user.lastName}`,
            role: userHierarchyTree ? 'member' : 'leader',
            children: []
        };

        if (!userHierarchyTree) {
            setUserHierarchyTree(newNode);
            enqueueSnackbar(`${user.firstName} ${user.lastName} set as Project Leader`, { variant: 'success' });
        } else {
            const updatedTree = { ...userHierarchyTree };
            if (!updatedTree.children) {
                updatedTree.children = [];
            }
            updatedTree.children.push(newNode);
            setUserHierarchyTree(updatedTree);
            enqueueSnackbar(`${user.firstName} ${user.lastName} added to team`, { variant: 'success' });
        }
        setIsDragging(false);
    };

    const handleTreeChange = (newTree) => {
        if (hierarchyMode === 'user_based') {
            setUserHierarchyTree(newTree);
        } else {
            setDesignationHierarchyTree(newTree);
        }
        setIsDragging(false);
    };

    const { assignedUserIds, leaderIdObj } = useMemo(() => {
        const obj = { id: null };
        const ids = getAssignedUserIds(userHierarchyTree, new Set(), obj);
        return { assignedUserIds: ids, leaderIdObj: obj };
    }, [userHierarchyTree]);

    const projectTeamUsers = useMemo(() => {
        if (hierarchyMode === 'user_based') {
            return users.filter(user => assignedUserIds.has(user._id) && user._id !== leaderIdObj.id);
        }
        // Under designation_based, any user scoped into levels can be a Form Creator
        const allAssignedIds = getDesignationTreeUserIds(designationHierarchyTree);
        return users.filter(user => allAssignedIds.has(String(user._id)));
    }, [hierarchyMode, users, assignedUserIds, leaderIdObj.id, designationHierarchyTree]);

    const availableUsers = users.filter(user => {
        if (assignedUserIds.has(user._id)) return false;
        if (!searchQuery) return true;
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase());
    });

    // ── Designation Levels Operations ──────────────────────────────────
    const handleAddDesignationLevel = () => {
        const nextLevelNumber = designationLevels.length + 1;
        setDesignationLevels(prev => [
            ...prev,
            {
                level: nextLevelNumber,
                designationId: null,
                assignedUsers: []
            }
        ]);
    };

    const handleRemoveDesignationLevel = (index) => {
        setDesignationLevels(prev =>
            prev
                .filter((_, i) => i !== index)
                .map((lvl, i) => ({ ...lvl, level: i + 1 })) // Renumber levels
        );
    };

    const handleLevelDesignationChange = (index, newDesignation) => {
        setDesignationLevels(prev =>
            prev.map((lvl, i) =>
                i === index ? { ...lvl, designationId: newDesignation, assignedUsers: [] } : lvl
            )
        );
    };

    const handleOpenGeoSelector = (index) => {
        const lvl = designationLevels[index];
        if (!lvl.designationId) {
            enqueueSnackbar('Please select a designation for this level first.', { variant: 'warning' });
            return;
        }
        setActiveLevelIdx(index);
        setGeoSelectorOpen(true);
    };

    const handleInitDesignationTree = () => {
        setDesignationHierarchyTree({
            id: `node-${Date.now()}`,
            designationId: null,
            assignedUsers: [],
            role: 'leader',
            children: []
        });
    };

    const handleManageDesignationStaff = (nodeId) => {
        setGeoTargetNodeId(nodeId);
        setGeoSelectorOpen(true);
    };

    const handleConfirmGeoSelections = (selectedUsersList) => {
        if (activeLevelIdx !== null) {
            // Legacy designationLevels update
            setDesignationLevels(prev =>
                prev.map((lvl, i) =>
                    i === activeLevelIdx ? { ...lvl, assignedUsers: selectedUsersList } : lvl
                )
            );
            enqueueSnackbar(`Assigned ${selectedUsersList.length} employees to Level ${activeLevelIdx + 1}`, { variant: 'success' });
        } else if (geoTargetNodeId) {
            // New designation hierarchy tree update
            const updatedTree = updateAssignedUsersInNode({ ...designationHierarchyTree }, geoTargetNodeId, selectedUsersList);
            setDesignationHierarchyTree(updatedTree);
            const node = findNodeById(updatedTree, geoTargetNodeId);
            enqueueSnackbar(`Assigned ${selectedUsersList.length} employees to ${node?.designationId?.designation_name || 'Designation'}`, { variant: 'success' });
        }
        setGeoSelectorOpen(false);
        setActiveLevelIdx(null);
        setGeoTargetNodeId(null);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh' }
                }}
            >
                <DialogTitle>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            Project Assignment Panel: {project?.name}
                        </Typography>
                        <IconButton onClick={onClose}>
                            <Iconify icon="solar:close-circle-bold" />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers>
                    {isMigrationContext && (
                        <Alert
                            severity="warning"
                            icon={<Iconify icon="solar:info-circle-bold" />}
                            sx={{ mb: 3, borderRadius: 1.5 }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Staff Migration Staging Mode
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                You are modifying the project hierarchy for this staff migration. Reassignments are scheduled based on the current tree. If team structure or member assignments change during the notice period, the system will dynamically reparent any unassigned subordinates at execution to protect against orphan nodes. You can update these reassignments at any time before execution.
                            </Typography>
                        </Alert>
                    )}

                    {/* Top Mode Switcher */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs value={hierarchyMode} onChange={handleHierarchyModeChange}>
                            <Tab
                                label="Manual Tree (User-Based)"
                                value="user_based"
                                icon={<Iconify icon="solar:graph-bold" />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Automated Geographic (Designation-Based)"
                                value="designation_based"
                                icon={<Iconify icon="solar:map-bold" />}
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>

                    {/* Mode Conditional Rendering */}
                    {hierarchyMode === 'user_based' ? (
                        /* Legacy visual tree drag and drop builder */
                        <Grid container spacing={2} sx={{ height: 'calc(100% - 70px)' }}>
                            <Grid item xs={12} md={3} sx={{ height: '100%' }}>
                                <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Stack spacing={2} sx={{ mb: 2 }}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                Available Users
                                            </Typography>
                                            <Tooltip title="Quick Add User">
                                                <IconButton size="small" color="primary" onClick={() => setQuickCreateOpen(true)}>
                                                    <Iconify icon="mingcute:add-line" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        <TextField
                                            size="small"
                                            placeholder="Search users..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            InputProps={{
                                                startAdornment: <Iconify icon="eva:search-fill" sx={{ mr: 1, color: 'text.disabled' }} />,
                                            }}
                                        />
                                        <Alert severity="info" sx={{ py: 0, alignItems: 'center' }}>
                                            Drag or click user to add
                                        </Alert>
                                    </Stack>

                                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                                        {loading && <Typography variant="body2" sx={{ p: 2 }}>Loading users...</Typography>}
                                        {!loading && availableUsers.length === 0 && (
                                            <Box sx={{ p: 2, textAlign: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {searchQuery ? 'No users match search' : 'All users assigned'}
                                                </Typography>
                                            </Box>
                                        )}
                                        <List dense>
                                            {availableUsers.map((user) => (
                                                <ListItem
                                                    key={user._id}
                                                    draggable
                                                    onDragStart={(e) => handleDragStart(e, user)}
                                                    onDragEnd={handleDragEnd}
                                                    onClick={() => handleAddUserToTree(user)}
                                                    sx={{
                                                        border: 1,
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        mb: 1,
                                                        cursor: 'grab',
                                                        '&:hover': {
                                                            bgcolor: 'action.hover',
                                                            borderColor: 'primary.main'
                                                        },
                                                        '&:active': { cursor: 'grabbing' }
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32, fontSize: '0.875rem' }}>
                                                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={`${user.firstName} ${user.lastName}`}
                                                        secondary={user.email}
                                                        primaryTypographyProps={{ noWrap: true, fontSize: '0.875rem' }}
                                                        secondaryTypographyProps={{ noWrap: true, fontSize: '0.75rem' }}
                                                    />
                                                    <Iconify icon="solar:hamburger-menu-bold" sx={{ color: 'text.disabled' }} width={16} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Box>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={9} sx={{ height: '100%' }}>
                                <Paper variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                    {!userHierarchyTree ? (
                                        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                                <Button
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                    startIcon={<Iconify icon="solar:copy-bold" />}
                                                    onClick={handleOpenCopyDialog}
                                                >
                                                    Copy from another project
                                                </Button>
                                            </Box>
                                            <Box sx={{ flex: 1 }}>
                                                <EmptyDropZone onDropUser={handleAddUserToTree} />
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.neutral' }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 0 }}>
                                                    <Typography variant="h6">Team Hierarchy</Typography>
                                                    <Stack direction="row" spacing={1}>
                                                        <Button
                                                            size="small"
                                                            color="info"
                                                            startIcon={<Iconify icon="solar:copy-bold" />}
                                                            onClick={handleOpenCopyDialog}
                                                        >
                                                            Copy from Project
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                                            onClick={() => {
                                                                if (window.confirm('Are you sure you want to clear the entire hierarchy?')) {
                                                                    setUserHierarchyTree(null);
                                                                    setFormCreators([]);
                                                                }
                                                            }}
                                                        >
                                                            Clear Hierarchy
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                                <Tabs value={rightTab} onChange={(e, v) => setRightTab(v)} sx={{ px: 2, mt: 1 }}>
                                                    <Tab label="Team Hierarchy" value="hierarchy" />
                                                    <Tab label="Form Creators" value="creators" />
                                                </Tabs>
                                            </Box>

                                            <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'background.default' }}>
                                                {rightTab === 'hierarchy' && (
                                                    <HierarchyTree
                                                        tree={userHierarchyTree}
                                                        onTreeChange={handleTreeChange}
                                                        availableUsers={availableUsers}
                                                        isDragging={isDragging}
                                                        onDragToggle={setIsDragging}
                                                        isMigrationContext={isMigrationContext}
                                                        migrationInfo={migrationInfo}
                                                        project={project}
                                                    />
                                                )}
                                                {rightTab === 'creators' && (
                                                    <Box>
                                                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                                            Select users who can create and manage forms for this project.
                                                            Only users already added to the hierarchy can be selected.
                                                        </Typography>
                                                        <Autocomplete
                                                            multiple
                                                            options={projectTeamUsers}
                                                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.employeeId || option.email})`}
                                                            value={formCreators.filter(fc => assignedUserIds.has(fc._id))}
                                                            onChange={(event, newValue) => setFormCreators(newValue)}
                                                            renderInput={(params) => <TextField {...params} variant="outlined" label="Form Creators" />}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => {
                                                                    const { key, ...tagProps } = getTagProps({ index });
                                                                    return (
                                                                        <Chip
                                                                            key={key}
                                                                            label={`${option.firstName} ${option.lastName}`}
                                                                            {...tagProps}
                                                                            size="medium"
                                                                            avatar={<Avatar sx={{ bgcolor: 'info.main' }}>{option.firstName?.charAt(0)}</Avatar>}
                                                                        />
                                                                    );
                                                                })
                                                            }
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    ) : (
                        /* Designation-based Hierarchy tree builder */
                        <Box sx={{ height: 'calc(100% - 70px)', display: 'flex', flexDirection: 'column' }}>
                            {!designationHierarchyTree ? (
                                <Box sx={{ py: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.neutral' }}>
                                    <Iconify icon="solar:layers-minimalistic-bold-duotone" width={64} sx={{ mb: 2, color: 'text.disabled' }} />
                                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                                        No designation hierarchy defined yet. Initialize a visual tree structure to begin.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<Iconify icon="solar:add-circle-bold" />}
                                        onClick={handleInitDesignationTree}
                                    >
                                        Initialize Designation Hierarchy
                                    </Button>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.neutral' }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 0 }}>
                                            <Typography variant="h6">Designation Hierarchy Tree</Typography>
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                                                    onClick={() => {
                                                        if (window.confirm('Are you sure you want to clear the entire designation hierarchy?')) {
                                                            setDesignationHierarchyTree(null);
                                                            setFormCreators([]);
                                                        }
                                                    }}
                                                >
                                                    Clear Hierarchy
                                                </Button>
                                            </Stack>
                                        </Stack>
                                        <Tabs value={rightTab} onChange={(e, v) => setRightTab(v)} sx={{ px: 2, mt: 1 }}>
                                            <Tab label="Designation Tree" value="hierarchy" />
                                            <Tab label="Form Creators" value="creators" />
                                        </Tabs>
                                    </Box>

                                    <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'background.default' }}>
                                        {rightTab === 'hierarchy' && (
                                            <DesignationHierarchyTree
                                                tree={designationHierarchyTree}
                                                onTreeChange={handleTreeChange}
                                                designations={designations}
                                                onManageStaff={handleManageDesignationStaff}
                                            />
                                        )}
                                        {rightTab === 'creators' && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                                                    Select employees assigned in the designation tree who are authorized to create and manage forms.
                                                </Typography>
                                                <Autocomplete
                                                    multiple
                                                    options={projectTeamUsers}
                                                    getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.employeeId || option.email})`}
                                                    value={formCreators}
                                                    onChange={(event, newValue) => setFormCreators(newValue)}
                                                    renderInput={(params) => <TextField {...params} variant="outlined" label="Authorized Form Creators" />}
                                                    renderTags={(value, getTagProps) =>
                                                        value.map((option, index) => {
                                                            const { key, ...tagProps } = getTagProps({ index });
                                                            return (
                                                                <Chip
                                                                    key={key}
                                                                    label={`${option.firstName} ${option.lastName}`}
                                                                    {...tagProps}
                                                                    size="medium"
                                                                    avatar={<Avatar sx={{ bgcolor: 'info.main' }}>{option.firstName?.charAt(0)}</Avatar>}
                                                                />
                                                            );
                                                        })
                                                    }
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || (hierarchyMode === 'user_based' ? !userHierarchyTree : !designationHierarchyTree)}
                        startIcon={saving ? <Iconify icon="eos-icons:loading" /> : <Iconify icon="solar:diskette-bold" />}
                    >
                        {saving ? 'Saving...' : 'Save Hierarchy Settings'}
                    </Button>
                </DialogActions>
            </Dialog >

            {EntityCreationDialog && (
                <Suspense fallback={null}>
                    <EntityCreationDialog
                        open={quickCreateOpen}
                        onClose={() => setQuickCreateOpen(false)}
                        onSubmit={handleCreateUser}
                        title="Create New User"
                        titleIcon="mingcute:add-line"
                        submitButtonText="Create User"
                        cancelButtonText="Cancel"
                        fields={quickAddFields}
                        showAvatar
                        successMessage="User created successfully!"
                        errorMessage="Failed to create user"
                    />
                </Suspense>
            )}

            {/* Copy Dialog */}
            <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Project Hierarchy</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1 }}>
                        <Typography sx={{ mb: 2 }}>
                            Select another active project to copy its team hierarchy configurations to this project.
                            <strong> This will replace the current settings.</strong>
                        </Typography>
                        <Autocomplete
                            options={projectsForCopy}
                            getOptionLabel={(option) => option.name || ''}
                            value={selectedProjectToCopy}
                            onChange={(event, newValue) => setSelectedProjectToCopy(newValue)}
                            loading={copyProjectLoading}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Project"
                                    placeholder="Search projects..."
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {copyProjectLoading ? <Iconify icon="eos-icons:loading" sx={{ mr: 1 }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={option._id}>
                                    <Typography variant="body2">{option.name}</Typography>
                                </li>
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCopyDialogOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCopyHierarchy}
                        variant="contained"
                        disabled={!selectedProjectToCopy || copyProjectLoading}
                        startIcon={copyProjectLoading ? <Iconify icon="eos-icons:loading" /> : <Iconify icon="solar:copy-bold" />}
                    >
                        Copy
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cascading Geographic Selector Dialog */}
            {geoSelectorOpen && (activeLevelIdx !== null || geoTargetNodeId !== null) && (
                <GeoUserSelectorDialog
                    open={geoSelectorOpen}
                    onClose={() => {
                        setGeoSelectorOpen(false);
                        setActiveLevelIdx(null);
                        setGeoTargetNodeId(null);
                    }}
                    designation={
                        activeLevelIdx !== null
                            ? designationLevels[activeLevelIdx]?.designationId
                            : findNodeById(designationHierarchyTree, geoTargetNodeId)?.designationId
                    }
                    users={users}
                    initialSelectedUsers={
                        activeLevelIdx !== null
                            ? designationLevels[activeLevelIdx]?.assignedUsers || []
                            : findNodeById(designationHierarchyTree, geoTargetNodeId)?.assignedUsers || []
                    }
                    onConfirm={handleConfirmGeoSelections}
                    projectStates={project?.states}
                    projectCities={project?.cities}
                    excludeUserIds={
                        activeLevelIdx !== null
                            ? getDesignationLevelsUserIdsExcludingIndex(designationLevels, activeLevelIdx)
                            : getDesignationTreeUserIdsExcludingNode(designationHierarchyTree, geoTargetNodeId)
                    }
                />
            )}

            {/* Hierarchy Switch Warning Dialog */}
            <Dialog open={!!pendingTabChange} onClose={() => setPendingTabChange(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Change Hierarchy Type?</DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                        Are you sure you want to change the hierarchy type to{' '}
                        <strong>
                            {pendingTabChange === 'user_based'
                                ? 'Manual Tree (User-Based)'
                                : 'Automated Geographic (Designation-Based)'}
                        </strong>
                        ?
                        <br />
                        <br />
                        This will clear the current visual hierarchy structure. You will need to build the new hierarchy and re-configure form rights for all forms in this project.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setPendingTabChange(null)} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmTabChange} variant="contained" color="warning">
                        Confirm Reset
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Form Rights Configuration Wizard Dialog */}
            <Dialog open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ pb: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography variant="h6">
                                Review Form Rights: {wizardForms[currentWizardStep]?.name || 'Form'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Step {currentWizardStep + 1} of {wizardForms.length} (Configure access rights under new hierarchy type)
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setWizardOpen(false)}>
                            <Iconify icon="solar:close-circle-bold" />
                        </IconButton>
                    </Stack>
                </DialogTitle>

                <DialogContent dividers sx={{ p: 0 }}>
                    {wizardForms.length > 0 && (() => {
                        const activeForm = wizardForms[currentWizardStep];
                        if (!activeForm) return null;
                        
                        const isSecondary = !!activeForm.isSecondaryForm;
                        const isCustomForm = activeForm.formType !== 'standard';
                        const formSwitches = activeForm.rightsSwitches || { import: false, export: true, approve: false };
                        const formRightsData = wizardRights[activeForm._id] || { rightsMap: [], designationTemplates: [] };

                        if (hierarchyMode === 'designation_based') {
                            const templates = formRightsData.designationTemplates || [];
                            if (templates.length === 0) {
                                return (
                                    <Box sx={{ p: 3 }}>
                                        <Alert severity="warning">
                                            No designations defined in the new hierarchy tree yet. Please initialize or build the designation hierarchy first.
                                        </Alert>
                                    </Box>
                                );
                            }
                            return (
                                <TableContainer component={Paper} sx={{ maxHeight: '60vh', borderRadius: 0, boxShadow: 'none' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Designation</TableCell>
                                                <TableCell align="center">View All {isSecondary && '(Synced)'}</TableCell>
                                                {isCustomForm && (
                                                    <>
                                                        <TableCell align="center">Country {isSecondary && '(Synced)'}</TableCell>
                                                        <TableCell align="center">State {isSecondary && '(Synced)'}</TableCell>
                                                        <TableCell align="center">City {isSecondary && '(Synced)'}</TableCell>
                                                        <TableCell align="center">Block {isSecondary && '(Synced)'}</TableCell>
                                                        <TableCell align="center">Locality {isSecondary && '(Synced)'}</TableCell>
                                                    </>
                                                )}
                                                {!isSecondary && <TableCell align="center">Add</TableCell>}
                                                <TableCell align="center">Edit</TableCell>
                                                <TableCell align="center">Delete</TableCell>
                                                {formSwitches.approve && <TableCell align="center">Approve</TableCell>}
                                                {!isSecondary && formSwitches.import && <TableCell align="center">Import</TableCell>}
                                                {!isSecondary && formSwitches.export && <TableCell align="center">Export</TableCell>}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {templates.map((template) => {
                                                const uRights = template.rights || {};
                                                return (
                                                    <TableRow key={template.designationId} hover>
                                                        <TableCell sx={{ fontWeight: 'medium' }}>
                                                            {template.designationName}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={!!uRights.viewAll}
                                                                disabled={isSecondary}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewAll', e.target.checked, false)}
                                                            />
                                                        </TableCell>
                                                        {isCustomForm && (
                                                            <>
                                                                <TableCell align="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!uRights.viewCountry}
                                                                        disabled={isSecondary}
                                                                        onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewCountry', e.target.checked, false)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!uRights.viewState}
                                                                        disabled={isSecondary}
                                                                        onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewState', e.target.checked, false)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!uRights.viewCity}
                                                                        disabled={isSecondary}
                                                                        onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewCity', e.target.checked, false)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!uRights.viewBlock}
                                                                        disabled={isSecondary}
                                                                        onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewBlock', e.target.checked, false)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={!!uRights.viewLocality}
                                                                        disabled={isSecondary}
                                                                        onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'viewLocality', e.target.checked, false)}
                                                                    />
                                                                </TableCell>
                                                            </>
                                                        )}
                                                        {!isSecondary && (
                                                            <TableCell align="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!uRights.add}
                                                                    onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'add', e.target.checked, false)}
                                                                />
                                                            </TableCell>
                                                        )}
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={!!uRights.edit}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'edit', e.target.checked, false)}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={!!uRights.delete}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'delete', e.target.checked, false)}
                                                            />
                                                        </TableCell>
                                                        {formSwitches.approve && (
                                                            <TableCell align="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!uRights.approve}
                                                                    onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'approve', e.target.checked, false)}
                                                                />
                                                            </TableCell>
                                                        )}
                                                        {!isSecondary && formSwitches.import && (
                                                            <TableCell align="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!uRights.import}
                                                                    onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'import', e.target.checked, false)}
                                                                />
                                                            </TableCell>
                                                        )}
                                                        {!isSecondary && formSwitches.export && (
                                                            <TableCell align="center">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={!!uRights.export}
                                                                    onChange={(e) => handleWizardRightChange(activeForm._id, template.designationId, 'export', e.target.checked, false)}
                                                                />
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            );
                        }

                        // user_based
                        const mapEntries = formRightsData.rightsMap || [];
                        const listUsers = getFlattenedUsers();
                        if (listUsers.length === 0) {
                            return (
                                <Box sx={{ p: 3 }}>
                                    <Alert severity="warning">
                                        No team members assigned in the new User Hierarchy tree yet. Please configure the project team hierarchy first.
                                    </Alert>
                                </Box>
                            );
                        }
                        return (
                            <TableContainer component={Paper} sx={{ maxHeight: '60vh', borderRadius: 0, boxShadow: 'none' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>User</TableCell>
                                            <TableCell align="center">View All {isSecondary && '(Synced)'}</TableCell>
                                            <TableCell align="center">View (Hierarchical) {isSecondary && '(Synced)'}</TableCell>
                                            {!isSecondary && <TableCell align="center">Add</TableCell>}
                                            <TableCell align="center">Edit</TableCell>
                                            <TableCell align="center">Delete</TableCell>
                                            {formSwitches.approve && <TableCell align="center">Approve</TableCell>}
                                            {!isSecondary && formSwitches.import && <TableCell align="center">Import</TableCell>}
                                            {!isSecondary && formSwitches.export && <TableCell align="center">Export</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {listUsers.map((user) => {
                                            const uEntry = mapEntries.find(e => e.userId === user.userId) || { userId: user.userId, rights: {} };
                                            const uRights = uEntry.rights || {};
                                            return (
                                                <TableRow key={user.userId} hover>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={2}>
                                                            <Avatar alt={user.name} sx={{ width: 28, height: 28 }}>{user.name?.charAt(0)}</Avatar>
                                                            <Box>
                                                                <Typography variant="body2" fontWeight="fontWeightMedium">
                                                                    {user.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {user.role} {user.isAdmin ? ' (Admin)' : ''}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            size="small"
                                                            checked={user.isAdmin || user.role === 'leader' || !!uRights.viewAll}
                                                            disabled={isSecondary || user.isAdmin || user.role === 'leader'}
                                                            onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'viewAll', e.target.checked, true)}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            size="small"
                                                            checked={(!user.isAdmin && user.role !== 'leader') && !!uRights.view}
                                                            disabled={isSecondary || user.isAdmin || user.role === 'leader'}
                                                            onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'view', e.target.checked, true)}
                                                        />
                                                    </TableCell>
                                                    {!isSecondary && (
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                 size="small"
                                                                 checked={user.isAdmin || user.role === 'leader' || !!uRights.add}
                                                                 disabled={user.isAdmin || user.role === 'leader'}
                                                                 onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'add', e.target.checked, true)}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            size="small"
                                                            checked={user.isAdmin || user.role === 'leader' || !!uRights.edit}
                                                            disabled={user.isAdmin || user.role === 'leader'}
                                                            onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'edit', e.target.checked, true)}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Checkbox
                                                            size="small"
                                                            checked={user.isAdmin || user.role === 'leader' || !!uRights.delete}
                                                            disabled={user.isAdmin || user.role === 'leader'}
                                                            onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'delete', e.target.checked, true)}
                                                        />
                                                    </TableCell>
                                                    {formSwitches.approve && (
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={user.isAdmin || user.role === 'leader' || !!uRights.approve}
                                                                disabled={user.isAdmin || user.role === 'leader'}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'approve', e.target.checked, true)}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    {!isSecondary && formSwitches.import && (
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={user.isAdmin || user.role === 'leader' || !!uRights.import}
                                                                disabled={user.isAdmin || user.role === 'leader'}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'import', e.target.checked, true)}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    {!isSecondary && formSwitches.export && (
                                                        <TableCell align="center">
                                                            <Checkbox
                                                                size="small"
                                                                checked={user.isAdmin || user.role === 'leader' || !!uRights.export}
                                                                disabled={user.isAdmin || user.role === 'leader'}
                                                                onChange={(e) => handleWizardRightChange(activeForm._id, user.userId, 'export', e.target.checked, true)}
                                                            />
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        );
                    })()}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2.5 }}>
                    <Button onClick={() => setWizardOpen(false)} color="inherit">
                        Cancel Wizard
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        disabled={currentWizardStep === 0}
                        onClick={() => setCurrentWizardStep(prev => prev - 1)}
                        color="inherit"
                        startIcon={<Iconify icon="solar:arrow-left-bold" />}
                    >
                        Back
                    </Button>
                    {currentWizardStep < wizardForms.length - 1 ? (
                        <Button
                            onClick={() => setCurrentWizardStep(prev => prev + 1)}
                            variant="contained"
                            color="primary"
                            endIcon={<Iconify icon="solar:arrow-right-bold" />}
                        >
                            Next Form
                        </Button>
                    ) : (
                        <Button
                            onClick={handleWizardSave}
                            variant="contained"
                            color="success"
                            disabled={saving}
                            startIcon={saving ? <Iconify icon="eos-icons:loading" /> : <Iconify icon="solar:diskette-bold" />}
                        >
                            {saving ? 'Saving...' : 'Finish & Save'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}

ProjectAssignmentModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    project: PropTypes.object.isRequired,
    onSaveSuccess: PropTypes.func,
    isMigrationContext: PropTypes.bool,
    stagedAssignment: PropTypes.object,
    onSaveStaged: PropTypes.func,
    migrationInfo: PropTypes.object,
    EntityCreationDialog: PropTypes.elementType,
};
